/**
 * Geometry and colour maths for the 3D station bands (`StationBands.tsx`).
 *
 * Pure and DOM-free on purpose: the corridor sampling, the diverging colour
 * ramp and the selection/hover emphasis are the parts that can be wrong in a
 * way a screenshot will not reveal, so they are unit-tested here and the r3f
 * component is left with nothing but wiring.
 *
 * The station sampling itself is NOT here — it is `sampleSwathEdges` in
 * `swathGeometry.ts` (ruling T9.1/T14.1), shared with the dialog's swath
 * preview. One sampling loop, two consumers.
 */
import { signIsCut } from '@/lib/cutFillConvention';
import { netPerMetre } from '@/lib/stationVolumes/aggregate';
import { sampleSwathEdges } from '@/lib/stationVolumes/swathGeometry';
import type { SvPalette } from '@/lib/stationVolumes/tokens';
import type { StationBin, StationParams } from '@/lib/stationVolumes/types';
import { pointAtStation, type Alignment } from '@/core/alignment';
import * as THREE from 'three';

/** Opacity multiplier for bins OUTSIDE the brush (spec §4e, ruling T14.2). */
export const BAND_DIM_ALPHA = 0.35;

/**
 * Sampling pitch along the centreline, in metres: fine enough that a curve
 * reads as a curve, coarse enough that 250 bins stay free (250 x ~12 samples
 * x 2 = 6 000 vertices). Never finer than 0.5 m — a 0.1 m interval is a user
 * mistake, not a reason to build a million-vertex ribbon.
 */
export function bandSampleStep(intervalM: number): number {
  return Math.min(5, Math.max(0.5, intervalM / 4));
}

/**
 * Stripe pitch for the low-coverage hatch, in metres, derived from the swath
 * width so the hatch reads the same on a 3 m footpath and a 60 m motorway
 * corridor (ruling T14.3: `map.repeat` derived from world size).
 *
 * Takes the corridor, not `StationParams` — the hatch period is a property of
 * the swath width, and clearance corridors have a swath too.
 */
export function bandHatchPeriodM(corridor: BandCorridor): number {
  return Math.max(0.5, (corridor.swathLeftM + corridor.swathRightM) / 8);
}

/**
 * three.js materials cannot read the `--sv-*` custom properties the 2D dock
 * draws with, so the 3D bands are handed the same palette object as plain hex
 * (`tokens.ts`) — the chart and the corridor cannot drift apart. WHICH half of
 * it (light or dark) is a runtime question, answered by `useSvPalette` at the
 * r3f boundary and passed in from there; this module never picks a theme.
 *
 * Parsing three hex strings per bin would be wasteful, so the parsed ramp is
 * cached against the palette object itself. `SV_COLORS.light` / `.dark` are
 * module constants, so the cache has exactly two live entries and the WeakMap
 * lets a caller-built palette fall out on its own.
 */
interface Ramp {
  cut: THREE.Color;
  fill: THREE.Color;
  zero: THREE.Color;
}
const RAMPS = new WeakMap<SvPalette, Ramp>();
function ramp(palette: SvPalette): Ramp {
  const hit = RAMPS.get(palette);
  if (hit) return hit;
  const built: Ramp = {
    cut: new THREE.Color(palette.cut),
    fill: new THREE.Color(palette.fill),
    zero: new THREE.Color(palette.zero),
  };
  RAMPS.set(palette, built);
  return built;
}

/**
 * Diverging cut<->fill ramp with a NEUTRAL — never a hue — at zero, over a
 * symmetric domain, so "no movement" is the colour that disappears and the
 * eye is drawn to the extremes.
 *
 * Explicitly not `sampleColormap('RAINBOW')` (`app/src/lib/colormaps.ts:9`):
 * a rainbow over a signed quantity invents an ordering that is not in the
 * data and puts its most salient band (yellow) at an arbitrary value.
 *
 * `netPerM` is net volume PER METRE so a short final bin is comparable with
 * a full-interval one. Values beyond the domain clamp — the p98 domain
 * exists precisely so one outlier bin cannot flatten the other 249, and
 * wrapping would paint the biggest cut in the fill hue.
 */
export function bandColor(
  netPerM: number,
  domain: [number, number],
  out: THREE.Color,
  palette: SvPalette,
): THREE.Color {
  // The ramp is defined by ONE half-span, so an asymmetric domain would make
  // `domain[0]` silently dead and paint a -9 the same as a -1. Every producer
  // (`symmetricDomain`) is symmetric by construction, so this is a
  // programming error and it says so rather than rendering a plausible lie.
  const halfSpan = Math.max(Math.abs(domain[0]), Math.abs(domain[1]));
  if (Math.abs(domain[0] + domain[1]) > 1e-9 * Math.max(halfSpan, 1)) {
    throw new Error(`bandColor: domain [${domain[0]}, ${domain[1]}] is not symmetric about zero`);
  }
  const { cut, fill, zero } = ramp(palette);
  const t = Math.max(-1, Math.min(1, netPerM / Math.max(halfSpan, 1e-9)));
  // WHICH end is red is not decided here (T4). `netVolumeM3` is `fill - cut`,
  // i.e. the integral of the signed surface difference, so its sign convention
  // IS `dz = top - bottom`'s — and a deviation run reaches this ramp through
  // the same one, because `station_bins.py` normalises it (the dock says as
  // much: "these figures carry the opposite sign to the deviation panel's
  // volumes"). So the polarity is asked of `cutFillConvention`, the single
  // place the "cut is red" ruling lives.
  return out.copy(zero).lerp(signIsCut('surfaceDifference', netPerM) ? cut : fill, Math.abs(t));
}

/** Base band opacity — the centreline has to stay readable through it (§4e). */
export const BAND_OPACITY = 0.55;
/**
 * Opacity for a run whose alignment no longer matches the one it measured.
 * The bands are NOT hidden — hiding the only visual link between the numbers
 * and the ground is worse than showing it qualified (ruling: dim + flag,
 * never hide) — but they drop back far enough that nobody reads a station off
 * them without noticing the warning.
 */
export const BAND_STALE_OPACITY = 0.3;

export interface BandPresentation {
  /** Base alpha for the band material. */
  opacity: number;
  /** Corridor silhouette colour, or `null` when no silhouette is drawn. */
  silhouette: string | null;
}

/**
 * How the corridor is presented for a given alignment-freshness verdict.
 * Pure so the "stale run still shows, but qualified" rule is testable without
 * a GPU: dimmed bands PLUS an amber silhouette of the corridor the bands are
 * currently drawn on, so the mismatch is visible in the scene and not only in
 * the dock's chip (A8 data honesty).
 */
export function bandPresentation(alignmentMatches: boolean, palette: SvPalette): BandPresentation {
  return alignmentMatches
    ? { opacity: BAND_OPACITY, silhouette: null }
    : { opacity: BAND_STALE_OPACITY, silhouette: palette.warn };
}

/** One dataset-agnostic PLATE: a station range, a flat elevation (or `null`
 *  to drape on the centreline) and a "could not measure this" flag. Only the
 *  volumes bands build these today (`volumeBandPlates` below) — clearance had
 *  its own adapters until its plates were absorbed into the swept envelope,
 *  which owns its geometry outright. The seam stays because it is what keeps
 *  this module free of `StationBin`. */
export interface BandPlate {
  staStartInternalM: number;
  staEndInternalM: number;
  flatZM: number | null;
  hatched: boolean;
}

/** The corridor's swath and station pitch — dataset-agnostic. */
export interface BandCorridor {
  swathLeftM: number;
  swathRightM: number;
  stationIntervalM: number;
  /**
   * Absolute elevation to stand an unmeasured plate on when the alignment has
   * no vertical profile of its own (`zSource: 'NONE'` — a LandXML centreline
   * at Z = 0). Used ONLY in that case: where the alignment really is graded,
   * the centreline follows the corridor and a single corridor-wide number
   * would flatten it. Omitted means "nothing measured", and the centreline is
   * all that is left.
   */
  fallbackZM?: number;
}

/** Colours plate `plateIndex` into `out` and returns it. The caller decides
 *  what "colour" means for its dataset (diverging net-per-metre for
 *  volumes, a violation ramp for clearance); this module only writes what
 *  it is given. */
export type BandColorFn = (plateIndex: number, out: THREE.Color) => THREE.Color;

export interface BandGeometry {
  /**
   * One merged, indexed quad-strip mesh for the whole corridor. Attributes:
   * `position` (relative to the floating origin), `aColor` (the diverging
   * ramp — written by `updateBandColors`, not by the build), `aState`
   * (x = opacity multiplier, y = hover brighten), `aHatch` (1 on low-coverage bins)
   * and `aHatchUv` (`(station, offset)` in metres, so the hatch runs at 45
   * degrees to the BAND rather than to the world grid).
   */
  geometry: THREE.BufferGeometry;
  /** `LineSegments` positions for the two LONG edges of every bin — the
   *  corridor's silhouette, drawn as a hairline. */
  edges: THREE.BufferGeometry;
  /** Transverse cap segments, one pair of points per BIN BOUNDARY (n+1 of
   *  them, not two per bin): the lines that make one plate end where the next
   *  begins. Drawn as a batched drei `<Line segments>` so they hold a real
   *  1.5 px stroke at any zoom — a 1-device-px hairline vanishes on a 4K
   *  display, which is exactly where a client looks at this. */
  capPoints: THREE.Vector3[];
  /** Vertex index -> index into the `bins` array. Picking reads `face.a`. */
  vertexBin: Uint16Array;
  /** `[startVertex, vertexCount]` per bin, parallel to the `bins` array. */
  binRanges: Int32Array;
}

/**
 * Build the merged corridor band mesh, one quad strip per PLATE, with
 * vertices relative to `origin` (the alignment's first point). UTM
 * easting/northing at Float32 jitters visibly, so every 3D alignment
 * consumer hangs off a floating-origin group — CLAUDE.md's rule,
 * `AlignmentOverlay.tsx:295`'s pattern.
 *
 * The plate list is deliberately dataset-agnostic (a station-volume bin and
 * a clearance bin share a station range, a flat elevation and a "could not
 * measure this" flag, and nothing else). The volumes adapters below are its
 * only producer now that the clearance plates have been absorbed into the
 * swept envelope; the seam is what keeps `StationBin` out of this module.
 *
 * Each plate is a FLAT slab at its own `flatZM`, which is what makes the
 * corridor read as a stack of measured slabs rather than a draped skin. A
 * plate with no surface data (`flatZM: null`) has no mean to sit at, so it
 * falls back to the centreline elevation rather than to an invented 0 m.
 *
 * COLOUR IS NOT BUILT HERE. `aColor` is allocated and left at zero; the
 * caller applies `updateBandColors(built, color)`. Colour is a presentation
 * quantity like `aState` — it depends on the app's theme, which can flip
 * while the corridor is on screen — and keeping it out of the build means a
 * theme change costs one buffer upload instead of re-sampling the whole
 * corridor. `domain` leaves `BandGeometry` for the same reason: colour is
 * now the caller's business.
 */
export function buildBandGeometry(
  a: Alignment,
  plates: BandPlate[],
  corridor: BandCorridor,
  origin: THREE.Vector3,
): BandGeometry {
  if (plates.length > 65535) {
    throw new Error(`buildBandGeometry: ${plates.length} plates exceeds the 65535 the vertex->bin map can address`);
  }
  const step = bandSampleStep(corridor.stationIntervalM);
  // See `BandCorridor.fallbackZM`: only a profile-less alignment defers to it.
  const fallbackZ =
    a.zSource === 'NONE' && corridor.fallbackZM != null ? corridor.fallbackZM - origin.z : null;

  // Pass 1: sample every plate (the shared swath sampler) and size the buffers.
  const sampled = plates.map((p) =>
    sampleSwathEdges(a, corridor.swathLeftM, corridor.swathRightM, p.staStartInternalM, p.staEndInternalM, step),
  );
  let vertexCount = 0;
  let indexCount = 0;
  let edgeVertexCount = 0;
  for (const s of sampled) {
    vertexCount += s.n * 2;
    indexCount += (s.n - 1) * 6;
    // the two long edges only, as line SEGMENTS — the transverse caps are a
    // separate, thicker pass (see `capPoints`)
    edgeVertexCount += (s.n - 1) * 4;
  }

  const positions = new Float32Array(vertexCount * 3);
  const colors = new Float32Array(vertexCount * 3);
  const state = new Float32Array(vertexCount * 2);
  const hatch = new Float32Array(vertexCount);
  const hatchUv = new Float32Array(vertexCount * 2);
  const vertexBin = new Uint16Array(vertexCount);
  const binRanges = new Int32Array(plates.length * 2);
  const indices = vertexCount > 65535 ? new Uint32Array(indexCount) : new Uint16Array(indexCount);
  const edgePositions = new Float32Array(edgeVertexCount * 3);
  const capPoints: THREE.Vector3[] = [];

  let v = 0; // vertex cursor
  let ix = 0; // index cursor
  let ev = 0; // edge-vertex cursor

  for (let bi = 0; bi < plates.length; bi++) {
    const b = plates[bi];
    const { n, stations, left, right } = sampled[bi];
    const base = v;
    binRanges[bi * 2] = base;
    binRanges[bi * 2 + 1] = n * 2;

    const flatZ = b.flatZM == null ? null : b.flatZM - origin.z;
    const lowCoverage = b.hatched ? 1 : 0;

    for (let i = 0; i < n; i++) {
      const li = i * 3;
      // A bin with no data has no mean top elevation. Drape it on the
      // centreline — unless the centreline has no elevation either, in which
      // case the run's own mean is the nearest thing to the ground there is.
      const z = flatZ ?? fallbackZ ?? pointAtStation(a, stations[i]).z - origin.z;
      for (let side = 0; side < 2; side++) {
        const src = side === 0 ? left : right;
        const o = (v + side) * 3;
        positions[o] = src[li] - origin.x;
        positions[o + 1] = src[li + 1] - origin.y;
        positions[o + 2] = z;
        state[(v + side) * 2] = 1;
        state[(v + side) * 2 + 1] = 0;
        hatch[v + side] = lowCoverage;
        // Hatch UV is (station, offset) in metres, NOT world XY: a world-XY
        // UV makes the 45 degree stripes run parallel to a north-east
        // corridor and disappear into it. In band space the stripes always
        // cross the band at 45 degrees, whatever the alignment's bearing.
        // Left is NEGATIVE offset (core/alignment.ts's convention).
        hatchUv[(v + side) * 2] = stations[i];
        hatchUv[(v + side) * 2 + 1] = side === 0 ? -corridor.swathLeftM : corridor.swathRightM;
        vertexBin[v + side] = bi;
      }
      if (i < n - 1) {
        const a0 = base + i * 2;
        indices[ix++] = a0;
        indices[ix++] = a0 + 1;
        indices[ix++] = a0 + 2;
        indices[ix++] = a0 + 1;
        indices[ix++] = a0 + 3;
        indices[ix++] = a0 + 2;
      }
      v += 2;
    }

    // Hairline silhouette: both long edges as chains of segments.
    const copy = (from: number) => {
      edgePositions[ev * 3] = positions[from * 3];
      edgePositions[ev * 3 + 1] = positions[from * 3 + 1];
      edgePositions[ev * 3 + 2] = positions[from * 3 + 2];
      ev++;
    };
    for (let i = 0; i < n - 1; i++) {
      copy(base + i * 2);
      copy(base + (i + 1) * 2);
      copy(base + i * 2 + 1);
      copy(base + (i + 1) * 2 + 1);
    }

    // One transverse cap per BIN BOUNDARY: this bin's start, plus the end of
    // the last bin. Emitting both caps of every bin would double-draw every
    // interior boundary, which reads as a heavier line on 249 of 250 edges.
    const capAt = (from: number) =>
      new THREE.Vector3(positions[from * 3], positions[from * 3 + 1], positions[from * 3 + 2]);
    capPoints.push(capAt(base), capAt(base + 1));
    if (bi === plates.length - 1) capPoints.push(capAt(base + (n - 1) * 2), capAt(base + (n - 1) * 2 + 1));
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('aState', new THREE.BufferAttribute(state, 2));
  geometry.setAttribute('aHatch', new THREE.BufferAttribute(hatch, 1));
  geometry.setAttribute('aHatchUv', new THREE.BufferAttribute(hatchUv, 2));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeBoundingSphere();

  const edges = new THREE.BufferGeometry();
  edges.setAttribute('position', new THREE.BufferAttribute(edgePositions, 3));
  edges.computeBoundingSphere();

  return { geometry, edges, capPoints, vertexBin, binRanges };
}

/**
 * Write the `aColor` attribute from a caller-supplied colour function.
 *
 * Called once after the build and again whenever the app's theme changes (or,
 * for clearance, whenever the violation ramp recomputes): one Float32 upload
 * over the same geometry, no re-sampling, no new material. Colour is entirely
 * the caller's business now — `BandGeometry` no longer carries a `domain` to
 * describe it.
 */
export function updateBandColors(built: BandGeometry, color: BandColorFn): void {
  const attr = built.geometry.getAttribute('aColor') as THREE.BufferAttribute;
  const arr = attr.array as Float32Array;
  const c = new THREE.Color();
  const plates = built.binRanges.length / 2;
  for (let bi = 0; bi < plates; bi++) {
    color(bi, c);
    const start = built.binRanges[bi * 2];
    const count = built.binRanges[bi * 2 + 1];
    for (let v = start; v < start + count; v++) {
      arr[v * 3] = c.r;
      arr[v * 3 + 1] = c.g;
      arr[v * 3 + 2] = c.b;
    }
  }
  attr.needsUpdate = true;
}

/**
 * Rewrite the `aState` attribute for a new brush selection / hover bin.
 *
 * This is the whole selection-emphasis mechanism (ruling T14.2): one Float32
 * attribute upload, no geometry rebuild, no second material, no re-render of
 * the scene graph. `x` is the opacity multiplier — 1 inside the brush,
 * `BAND_DIM_ALPHA` outside — and `y` is the hover flag the shader uses to
 * brighten the band toward white.
 */
export function updateBandEmphasis(
  built: BandGeometry,
  selection: { fromBin: number; toBin: number } | null,
  hoverBin: number | null,
): void {
  const attr = built.geometry.getAttribute('aState') as THREE.BufferAttribute;
  const arr = attr.array as Float32Array;
  const lo = selection ? Math.min(selection.fromBin, selection.toBin) : 0;
  const hi = selection ? Math.max(selection.fromBin, selection.toBin) : 0;
  for (let i = 0; i < built.vertexBin.length; i++) {
    const b = built.vertexBin[i];
    arr[i * 2] = !selection || (b >= lo && b <= hi) ? 1 : BAND_DIM_ALPHA;
    arr[i * 2 + 1] = hoverBin != null && b === hoverBin ? 1 : 0;
  }
  attr.needsUpdate = true;
}

/**
 * One bin's silhouette as a closed loop — left edge out, right edge back,
 * first point repeated — ready for drei's `<Line>` (which draws a real
 * screen-space 2 px stroke; `LineBasicMaterial`'s `linewidth` is ignored by
 * every WebGL implementation).
 *
 * Returns `[]` for an index that is not a bin, so a stale hover from a
 * previous run cannot throw in the middle of a frame.
 */
export function binOutlinePoints(built: BandGeometry, binIndex: number): THREE.Vector3[] {
  if (binIndex < 0 || binIndex * 2 + 1 >= built.binRanges.length) return [];
  const start = built.binRanges[binIndex * 2];
  const count = built.binRanges[binIndex * 2 + 1];
  if (count < 4) return [];
  const pos = built.geometry.getAttribute('position') as THREE.BufferAttribute;
  const n = count / 2;
  const at = (v: number) => new THREE.Vector3(pos.getX(v), pos.getY(v), pos.getZ(v));
  const loop: THREE.Vector3[] = [];
  for (let i = 0; i < n; i++) loop.push(at(start + i * 2));
  for (let i = n - 1; i >= 0; i--) loop.push(at(start + i * 2 + 1));
  loop.push(loop[0].clone());
  return loop;
}

/**
 * Volumes adapters — the exact inputs `StationBands` used to pass inline,
 * before the plate/corridor/colour-fn seam existed. This is the only place a
 * dataset's bins/params get converted into the dataset-agnostic shapes above.
 */
export function volumeBandPlates(bins: StationBin[]): BandPlate[] {
  return bins.map((b) => ({
    staStartInternalM: b.staStartInternalM,
    staEndInternalM: b.staEndInternalM,
    flatZM: b.zTopMeanM ?? null,
    hatched: !!b.lowCoverage,
  }));
}

/**
 * `bins` is optional so the one caller that has none keeps working; when it is
 * given, the mean of the bins that actually measured a top becomes
 * `fallbackZM` — the elevation an unmeasured plate stands on when the
 * alignment itself has no Z. Same quantity `clipDimsForRun` puts the clip box
 * on, and for the same reason (ruling D35).
 */
export function volumeBandCorridor(params: StationParams, bins?: StationBin[]): BandCorridor {
  let sum = 0;
  let n = 0;
  for (const b of bins ?? []) {
    if (b.zTopMeanM == null || !Number.isFinite(b.zTopMeanM)) continue;
    sum += b.zTopMeanM;
    n++;
  }
  return {
    swathLeftM: params.swathLeftM,
    swathRightM: params.swathRightM,
    stationIntervalM: params.stationIntervalM,
    fallbackZM: n > 0 ? sum / n : undefined,
  };
}

/** Diverging net-per-metre colour, closed over the bins and the domain they
 *  were aggregated to — `StationBands` recomputes this whenever the bins,
 *  domain or theme change (see its `updateBandColors` effect). */
export function volumeBandColorFn(bins: StationBin[], domain: [number, number], palette: SvPalette): BandColorFn {
  return (i, out) => bandColor(netPerMetre(bins[i]), domain, out, palette);
}
